/**
 * The two figures that mean the same thing on both channels — and the visible
 * absence of the one that does not.
 *
 * **Accepted and delivered may be added across email and WhatsApp.** "The
 * provider took it" is one event with two names, and "it reached the recipient's
 * mail server or device" is one outcome with two names, so a tenant asking *how
 * much went out this month* is asking a question that has a single answer. The
 * gap between the two totals is worth seeing for the same reason slices 5, 6 and
 * 12 kept them apart per campaign: everything accepted and nothing delivered is
 * a different morning from everything delivered.
 *
 * **A combined failure column is not defensible and is not here.** There is no
 * bounce on WhatsApp and no read receipt on email; Amazon reports five different
 * not-delivered events with five different owners, and Meta reports one word
 * with an error code behind it. Added together they make a number whose only
 * content is "something is wrong somewhere", and the tenant who reads it cleans
 * a list they should have retried. `CROSS_CHANNEL_MEASURE_VALUES` has two
 * members and no `'failed'`, so this component could not render one if it wanted
 * to: `crossChannelTotal('failed', …)` does not compile and
 * `{ measure: 'failed' }` does not parse.
 *
 * **The absence is explained rather than left as a hole**, which is the whole
 * reason `NO_CROSS_CHANNEL_FAILURE_NOTE` is a constant in the contract instead
 * of a paragraph somebody might delete as clutter. An unexplained gap where a
 * reader expects a column reads as an oversight and invites the next person to
 * fill it in.
 *
 * **`null` is not zero, on either side of the row.** A `null` contribution means
 * *this workspace does not send on that channel*, and it prints as a phrase, not
 * as a nought — a tenant with no Meta integration shown "0 delivered" has been
 * told a measurement was taken. `measuredChannels` is what the total is actually
 * over, so the row says "email only" rather than implying a WhatsApp figure of
 * nothing. When neither channel is measured there is no total at all: the whole
 * object is `null`, because a total nothing contributed to is not a total.
 *
 * **Nothing here is ever a denominator.** Slice 10 shipped a critical where
 * bounce and complaint rates were computed over every channel while drawn
 * against AWS's email thresholds, and a workspace sitting exactly on the 5% line
 * rendered as 1.55% behind a green badge because 2,000 WhatsApp sends were
 * underneath it. A cross-channel figure is the exact shape of that mistake, so
 * these two numbers are shown as counts and no percentage is computed from them
 * anywhere on this screen.
 */
import {
  CROSS_CHANNEL_MEASURE_LABELS,
  NO_CROSS_CHANNEL_FAILURE_NOTE,
  type CrossChannelTotal,
} from '@wend/shared';

import { BySourceBars, MetricPanel, MetricSection } from '@wend/ui';

import { CHANNEL_LABEL } from '../../lib/campaign-display';

/** How the channels behind a total are named in the sentence under it. */
function measuredChannelsPhrase(total: CrossChannelTotal): string {
  const names = total.measuredChannels.map((channel) => CHANNEL_LABEL[channel]);
  return names.length === 1 ? `${names[0] ?? ''} only` : names.join(' and ');
}

const CHANNELS = ['email', 'whatsapp'] as const;

/**
 * One total as a figure panel, with each channel's contribution under it.
 *
 * Slice 66 drew this as the shared `MetricPanel` with `BySourceBars` beneath:
 * the total is the figure, and each measured channel is a row with its count.
 * The bars are scaled against the larger of the two counts and carry **no "of
 * N"** — deliberately not a share of the total, because this header's last rule
 * is that nothing here is ever a denominator, and a bar reading "of 700" is a
 * percentage with the digits left off. The rows are counts only, which is also
 * why the panel has no meter of its own.
 *
 * A channel that does not send is a sentence beside the bars, never a bar.
 * `BySourceBars` draws a `null` count as "not measured", and that is not this
 * fact: nothing was missed — the workspace simply does not send there.
 */
function TotalRow({ total }: { total: CrossChannelTotal }) {
  const rows = CHANNELS.flatMap((channel) => {
    const count = total[channel];
    return count === null ? [] : [{ key: channel, label: CHANNEL_LABEL[channel], count }];
  });
  const unsent = CHANNELS.filter((channel) => total[channel] === null);

  return (
    <MetricPanel
      kicker={CROSS_CHANNEL_MEASURE_LABELS[total.measure]}
      figure={{ kind: 'measured', value: total.total }}
      figureSize="lg"
      sub={`Distinct messages, over ${measuredChannelsPhrase(total)}.`}
      // Delivered is the number to leave with; accepted is the plain card.
      variant={total.measure === 'delivered' ? 'brand' : 'plain'}
    >
      <MetricSection kicker="By channel">
        <BySourceBars rows={rows} />
        {unsent.map((channel) => (
          <p key={channel} className="mt-2 text-[0.8rem] text-muted">
            {CHANNEL_LABEL[channel]} · Not sent from this workspace
          </p>
        ))}
      </MetricSection>
    </MetricPanel>
  );
}

export interface AnalyticsCrossChannelProps {
  /** `null` when neither channel is measured — not a zero. */
  accepted: CrossChannelTotal | null;
  /** `null` when neither channel is measured — not a zero. */
  delivered: CrossChannelTotal | null;
}

export function AnalyticsCrossChannel({ accepted, delivered }: AnalyticsCrossChannelProps) {
  const totals = [accepted, delivered].filter((total): total is CrossChannelTotal => total !== null);

  return (
    <div className="flex flex-col gap-4">
      {totals.length === 0 ? (
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          This workspace sends on neither channel, so there is nothing to add up. That is not a
          total of zero — nothing was measured to put in one.
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {totals.map((total) => (
              <TotalRow key={total.measure} total={total} />
            ))}
          </div>

          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            These two may be added across channels because they mean the same thing on both: the
            provider took the message, and the message arrived. The gap between them is what to
            watch.
          </p>
        </>
      )}

      <p className="max-w-prose border-t border-line pt-4 text-[0.88rem] leading-relaxed text-muted">
        {NO_CROSS_CHANNEL_FAILURE_NOTE}
      </p>
    </div>
  );
}
